/**
 * useDockPanels - Context for registering page-scoped panels into UniversalDock
 *
 * WHY: PR 3d (Stack 3) makes the bottom-dock REGISTRY-DRIVEN. Components
 * deep in the tree (e.g. a Notifications widget on a hub home page, or
 * an Inspector tied to a job-detail page) need to push a slide-out
 * panel into the dock without prop-drilling through every layout
 * boundary. This context lets any descendant of <DashboardLayout>
 * register or unregister DockPanel entries at runtime.
 *
 * SHAPES the dock recognises:
 *   - "Action" = one-shot click button (no panel body) — see useDockActions.
 *   - "Panel"  = toggle button + slide-out body — THIS hook.
 *
 * USAGE (in a child component):
 *   const { setPanels, clearPanels } = useDockPanels()
 *
 *   useEffect(() => {
 *     setPanels([
 *       {
 *         id: 'notifications',
 *         label: 'Bell',
 *         icon: <Bell className="w-3.5 h-3.5" />,
 *         activeColor: 'amber',
 *         render: ({ isOpen, onClose }) =>
 *           <NotificationsPanel isOpen={isOpen} onClose={onClose} />,
 *       },
 *     ])
 *     return () => clearPanels()
 *   }, [deps])
 *
 * Built-in panels (Bug Report / Teams / AI) are ALWAYS rendered first
 * by <UniversalDock>. Custom panels with an id matching a built-in id
 * (e.g. 'ai') will *override* the built-in — useful for hubs that want
 * to swap in a richer AI surface for their own users.
 *
 * @path packages/ui/src/hooks/useDockPanels.tsx
 */

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react'
import type { DockPanel } from '../components/UniversalDock'

interface DockPanelsContextValue {
  /** Currently registered panels (in registration order) */
  panels: DockPanel[]
  /** Set/replace all panels for the current page */
  setPanels: (panels: DockPanel[]) => void
  /** Clear all registered panels */
  clearPanels: () => void
  /** Add a single panel (idempotent by id — replaces if id already exists) */
  addPanel: (panel: DockPanel) => void
  /** Remove a single panel by id */
  removePanel: (id: string) => void
}

const noop = () => {}

const DockPanelsContext = createContext<DockPanelsContextValue>({
  panels: [],
  setPanels: noop,
  clearPanels: noop,
  addPanel: noop,
  removePanel: noop,
})

export function DockPanelsProvider({ children }: { children: ReactNode }) {
  const [panels, setPanelsState] = useState<DockPanel[]>([])

  const setPanels = useCallback((next: DockPanel[]) => {
    setPanelsState(next)
  }, [])

  const clearPanels = useCallback(() => {
    setPanelsState([])
  }, [])

  const addPanel = useCallback((panel: DockPanel) => {
    setPanelsState((prev) => {
      const exists = prev.some((p) => p.id === panel.id)
      if (exists) return prev.map((p) => (p.id === panel.id ? panel : p))
      return [...prev, panel]
    })
  }, [])

  const removePanel = useCallback((id: string) => {
    setPanelsState((prev) => prev.filter((p) => p.id !== id))
  }, [])

  return (
    <DockPanelsContext.Provider
      value={{ panels, setPanels, clearPanels, addPanel, removePanel }}
    >
      {children}
    </DockPanelsContext.Provider>
  )
}

export function useDockPanels() {
  return useContext(DockPanelsContext)
}

export { DockPanelsContext }
